// Lib
import React, { ReactElement } from 'react';
import { connect, ConnectedProps } from 'react-redux';

// Utils
import {
    getFirstSelectedColor,
    getSelectedElementType,
} from '../../workspace/toolbar/components/selectionTools/colorTools/colorToolUtils';
import { getElementColorPickerHexValue } from '../../../common/colors/elementColorPickerUtils';
import { isBoardLike, isLine } from '../../../common/elements/utils/elementTypeUtils';

// Selectors
import { getThemeIsDarkMode } from '../../theme/themeSelector';

// Actions
import { closePopup } from '../popupPanel/popupActions';
import setTemporarySelectedElementsColorStyles from '../../workspace/toolbar/components/selectionTools/colorTools/setTemporarySelectedElementsColorStyles';
import { updateSelectedElementsColors } from '../../workspace/toolbar/components/selectionTools/colorTools/colorActions';

// Components
import CustomColorInput from '../../workspace/toolbar/components/selectionTools/colorTools/CustomColorInput';

// Constants
import {
    DEFAULT_BOARD_COLOR,
    DEFAULT_BOARD_COLOR_DARK,
    DEFAULT_ELEMENT_COLOR,
    DEFAULT_LINE_COLOR,
    DEFAULT_LINE_COLOR_DARK,
} from '../../../common/colors/colorConstants';
import { PopupIds } from '../popupPanel/popupConstants';
import { List, Map } from 'immutable';
import { ImMNElement } from '../../../common/elements/elementModelTypes';

const mapStateToProps = (state: Map<string, unknown>) => ({
    isDarkMode: getThemeIsDarkMode(state),
});

const mapDispatchToProps = (dispatch: Function) => ({
    setTempColor: setTemporarySelectedElementsColorStyles,
    setColor: (color: string) => {
        dispatch(updateSelectedElementsColors({ color, secondaryColor: null, useExistingColorSpace: true }));
        dispatch(closePopup(PopupIds.COLOR, false));
    },
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

const getInitialColor = (elements: List<ImMNElement>, isDarkMode: boolean): string => {
    // first check if our selection has a color already assigned that we should use
    const selectedColor = getFirstSelectedColor(elements);
    const firstElementType = getSelectedElementType(elements);
    const selectedColorHex = getElementColorPickerHexValue({
        color: selectedColor,
        elementType: firstElementType,
        isDarkMode,
    });
    if (selectedColorHex) return selectedColorHex;

    // otherwise go with a default color
    if (isBoardLike(firstElementType)) return isDarkMode ? DEFAULT_BOARD_COLOR_DARK : DEFAULT_BOARD_COLOR;
    if (isLine(firstElementType)) return isDarkMode ? DEFAULT_LINE_COLOR_DARK : DEFAULT_LINE_COLOR;

    return DEFAULT_ELEMENT_COLOR;
};

type ElementColorPickerProps = ReduxProps & {
    openerEventTarget?: EventTarget;
    selectedColorHex?: string;
    elements?: List<ImMNElement>;
    onOpen?: (color: string) => void;
    onBlur?: (color: string) => void;
};

const ElementColorPicker = ({
    openerEventTarget,
    setColor,
    setTempColor,
    onOpen,
    onBlur,
    elements,
    isDarkMode,
}: ElementColorPickerProps): ReactElement => (
    <CustomColorInput
        openerEventTarget={openerEventTarget}
        setTempColor={setTempColor}
        setColor={setColor}
        initialColor={elements && getInitialColor(elements, isDarkMode)}
        onOpen={onOpen}
        onBlur={onBlur}
    />
);

export default connector(ElementColorPicker);
